import React, { useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { isProjectStandardRole } from "../../redux/actions/userAction";
import Add from "@material-ui/icons/Add";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button } from "@material-ui/core";
import axios from "axios";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import RichTextEditor from "./RichTextEditor";
import "./CommentModal.css";

// Maximum HTML content length for database storage
// Business requirement: 5000 visible (plain-text) characters.
const MAX_PLAIN_TEXT_LENGTH = 5000;
// Safety cap for raw HTML payload (includes rich-text tags/markup).
const MAX_HTML_LENGTH = 30000;

const CommentModal = (props) => {
  const [show, setShow] = useState(false);
  const [showExternal, setShowExternal] = useState("internal");
  const [htmlContent, setHtmlContent] = useState("");
  const [plainTextLength, setPlainTextLength] = useState(0);
  const [commentError, setCommentError] = useState(false);
  const [htmlLengthError, setHtmlLengthError] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const editorKey = useRef(0);
  const handleShow = () => setShow(true);


  const handleClose = () => {
    setShowExternal('internal');
    setHtmlContent('');
    setPlainTextLength(0);
    setCommentError(false);
    setHtmlLengthError(false);
    editorKey.current += 1; // Reset editor by changing key
    setShow(false);
  }

  const handleSave = (e) => {
    if (plainTextLength > 0) {
      if (plainTextLength > MAX_PLAIN_TEXT_LENGTH) {
        setCommentError(true);
        setSnackbarMessage(`Comment exceeds ${MAX_PLAIN_TEXT_LENGTH} plain-text characters.`);
        setShowSnackbar(true);
        return;
      }

      // Check if HTML content exceeds database limit
      if (htmlContent.length > MAX_HTML_LENGTH) {
        setHtmlLengthError(true);
        setSnackbarMessage(`Comment is too large with formatting. Please reduce content or formatting. (${htmlContent.length}/${MAX_HTML_LENGTH} HTML characters)`);
        setShowSnackbar(true);
        return;
      }
      
      const passData = JSON.parse(sessionStorage.getItem("passData"));
      const request  = {
        userName: passData.user,
        projectId: props.projectvalue,
        phaseId: props.phasevalue ?? null,
        persId: props.persvalue ?? null,
        comments: htmlContent,
        showExternal: showExternal === "both" ? true : false,
      }
      axios
        .post(
          global.REACT_APP_API_ENDPOINT + `/condoMgr/postComments/`,
          request
        )
        .then((response) => {
          handleClose();
          window.location.reload(false);
        })
        .catch((error) => {
          console.error('Failed to save comment:', error);
          setSnackbarMessage('Failed to save comment. Please try again.');
          setShowSnackbar(true);
        });
    } else {
      setCommentError(true);
    }
  };

  const handleEditorChange = (html) => {
    setHtmlContent(html);
    if (commentError && html.trim() !== '') {
      setCommentError(false);
    }
    // Track HTML length and show warning/error when approaching or exceeding limit
    if (html.length > MAX_HTML_LENGTH) {
      setHtmlLengthError(true);
    } else if (htmlLengthError) {
      setHtmlLengthError(false);
    }
  };

  const handlePlainTextChange = (text) => {
    setPlainTextLength(text.length);
    if (commentError && text.length > 0) {
      setCommentError(false);
    }
  };

  const radioChecked = (e) => {
    setShowExternal(e.target.value);
  }

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setShowSnackbar(false);
  };

  return (
    <>
      {isProjectStandardRole() ? (
        <button className="add-phase-button" onClick={handleShow}>
           <Add className="edit-profile-icon" />
          Add Comment
        </button>
      ) : null}

      <Modal aria-label="upload new attachment modal" show={show} onHide={handleClose} style={{paddingTop: "7.5%"}}  backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Add Comment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body">
        <form className="w-75 pl-3 ">
            <div className="form-group">
              <label style={{ marginTop: 8, display: "block" }} >Show Comments</label>
              <ToggleButtonGroup 
                color="primary"
                value={showExternal}
                exclusive
                onChange={radioChecked}
                aria-label="text alignment"
              >
                <ToggleButton value="internal" aria-label="left aligned">
                  Internal
                </ToggleButton>
                <ToggleButton value="both" aria-label="centered">
                  Internal & External
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            {showExternal === "both" ? (
                <div className="comment-note"> Note: This will allow lenders to view this comment; use standardized language when possible.</div>
              ) : (<></>)}
          <div className="form-group">
            <label className="">Comment</label>
            <RichTextEditor
              key={editorKey.current}
              onChange={handleEditorChange}
              onPlainTextChange={handlePlainTextChange}
              maxLength={MAX_PLAIN_TEXT_LENGTH}
              placeholder="Type your comment here..."
              ariaLabel="Comment"
              error={commentError || htmlLengthError}
            />
            {commentError ? (
              <div style={{color: "#ff0000", marginTop: "8px"}}>Please enter a comment.</div>
            ) : null}
            {htmlLengthError ? (
              <div style={{color: "#ff0000", marginTop: "8px" , width: "130%"}}>
                Comment HTML content exceeds maximum payload length ({htmlContent.length}/{MAX_HTML_LENGTH}). 
                Please reduce formatting-heavy content.
              </div>
            ) : null}
          </div>
        </form>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: "#f7f7f7" }}>
        <Button className="comments-button" onClick={handleSave} disabled={htmlLengthError}>
          Save
        </Button>
      </Modal.Footer>
      </Modal>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CommentModal;
